import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Gift, Mail, User, MessageSquare, CreditCard, Download, Check, Sparkles, Star, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

const GiftCards = () => {
  const navigate = useNavigate();
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [senderName, setSenderName] = useState('');
  const [message, setMessage] = useState('');
  const [deliveryType, setDeliveryType] = useState('email');
  const [showPreview, setShowPreview] = useState(false);
  const [purchaseComplete, setPurchaseComplete] = useState(false);
  const [giftCardData, setGiftCardData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');

  // Debug: Log configuration on mount
  React.useEffect(() => {
    console.log('Gift Cards Component Mounted');
    console.log('API URL:', API_URL);
    console.log('PayPal Client ID:', PAYPAL_CLIENT_ID ? `${PAYPAL_CLIENT_ID.substring(0, 20)}...` : 'NOT SET');
  }, []);

  const amounts = [
    { value: 50, popular: false },
    { value: 100, popular: true },
    { value: 150, popular: false },
    { value: 200, popular: false }
  ];

  const handlePreview = () => {
    if (selectedAmount && recipientName && recipientEmail && senderName) {
      setShowPreview(true);
      setError('');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setError('Please fill in all required fields');
    }
  };

  // Handle PayPal redirect payment
  const handlePayPalPayment = async () => {
    try {
      console.log('💳 Initiating PayPal redirect payment...');
      setIsProcessing(true);
      setError('');
      
      // Save form data to sessionStorage for after redirect
      sessionStorage.setItem('giftCardFormData', JSON.stringify({
        amount: selectedAmount,
        recipient_name: recipientName,
        recipient_email: recipientEmail,
        sender_name: senderName,
        message: message,
        delivery_type: deliveryType
      }));
      
      // Create order via backend
      const response = await axios.post(`${API_URL}/api/gift-cards/create-order`, {
        amount: selectedAmount,
        recipient_name: recipientName,
        recipient_email: recipientEmail,
        sender_name: senderName,
        message: message,
        delivery_type: deliveryType
      });
      
      console.log('✅ Order created:', response.data);
      
      // Get the approval URL and redirect
      if (response.data.approval_url) {
        console.log('🚀 Redirecting to PayPal...');
        window.location.href = response.data.approval_url;
      } else {
        throw new Error('No approval URL received from PayPal');
      }
    } catch (err) {
      setIsProcessing(false);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to create order';
      setError(`Failed to create order: ${errorMsg}`);
      console.error('❌ Error:', err);
    }
  };


  const onApprove = async (data, actions) => {
    try {
      console.log('Payment approved, capturing...', data);
      setIsProcessing(true);
      setError('');
      
      const response = await axios.post(`${API_URL}/api/gift-cards/capture-payment`, {
        order_id: data.orderID,
        purchase_data: {
          amount: selectedAmount,
          recipient_name: recipientName,
          recipient_email: recipientEmail,
          sender_name: senderName,
          message: message,
          delivery_type: deliveryType
        }
      });
      
      console.log('Payment captured successfully:', response.data);
      setGiftCardData(response.data);
      setPurchaseComplete(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError('Payment processing failed. Please contact support.');
      console.error('Payment capture error:', err);
      console.error('Error details:', err.response?.data || err.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const onError = (err) => {
    console.error('❌ PayPal SDK Error:', err);
    console.error('❌ Error type:', typeof err);
    console.error('❌ Error stringified:', JSON.stringify(err, null, 2));
    setError('Payment failed. Please try again.');
    setIsProcessing(false);
  };

  const onCancel = (data) => {
    console.log('⚠️ Payment cancelled by user:', data);
    setIsProcessing(false);
    setError('Payment was cancelled. Please try again when ready.');
  };

  if (purchaseComplete && giftCardData) {
    return (
      <div className="gift-cards-page">
        <Helmet>
          <title>Gift Card Purchase Complete | Mystic Goddess Beauty Studio</title>
          <meta name="description" content="Your Mystic Goddess Beauty Studio gift card has been purchased successfully." />
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <div className="gift-cards-hero">
          <div className="stars-container-gift">
            {[...Array(30)].map((_, i) => (
              <div
                key={i}
                className="star"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 3}s`
                }}
              />
            ))}
          </div>
          <Button
            variant="ghost"
            className="back-to-home"
            onClick={() => navigate('/')}
          >
            ← Back to Home
          </Button>
        </div>

        <div className="purchase-success-container">
          <Card className="success-card">
            <CardContent className="success-content">
              <div className="success-icon">
                <Check className="w-16 h-16" />
              </div>
              <h2 className="success-title">Gift Card Purchased Successfully!</h2>
              <p className="success-subtitle">Your magical gift is ready ✨</p>

              <div className="gift-card-preview-box">
                <div className="gift-card-digital">
                  <div className="gift-card-header">
                    <Sparkles className="w-8 h-8" />
                    <h3>MYSTIC GODDESS</h3>
                    <p>Beauty Studio Gift Card</p>
                  </div>
                  
                  <div className="gift-card-amount">
                    <span className="currency">$</span>
                    <span className="value">{giftCardData.amount}</span>
                  </div>

                  <div className="gift-card-code">
                    <Label>Gift Card Code</Label>
                    <div className="code-display">{giftCardData.code}</div>
                  </div>

                  <div className="gift-card-recipient">
                    <p><strong>To:</strong> {giftCardData.recipient_name}</p>
                    <p><strong>From:</strong> {giftCardData.sender_name}</p>
                    {giftCardData.message && (
                      <div className="gift-message">
                        <p>{giftCardData.message}</p>
                      </div>
                    )}
                  </div>

                  <div className="gift-card-footer">
                    <Sparkles className="w-4 h-4" />
                    <span>Celestial Beauty</span>
                  </div>
                </div>
              </div>

              <div className="success-actions">
                <div className="email-sent-notice">
                  <Mail className="w-5 h-5" />
                  <span>Gift card has been sent to {giftCardData.recipient_email}</span>
                </div>

                <div className="action-buttons">
                  <Button className="another-btn" onClick={() => {
                    setPurchaseComplete(false);
                    setGiftCardData(null);
                    setSelectedAmount(null);
                    setRecipientName('');
                    setRecipientEmail('');
                    setSenderName('');
                    setMessage('');
                    setShowPreview(false);
                  }}>
                    <Gift className="w-4 h-4 mr-2" />
                    Purchase Another
                  </Button>
                  
                  <Button className="home-btn" variant="ghost" onClick={() => navigate('/')}>
                    Return to Home
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <PayPalScriptProvider 
      options={{ 
        clientId: PAYPAL_CLIENT_ID,
        currency: "USD",
        intent: "capture",
        "data-page-type": "checkout",
        components: "buttons"
      }}
      deferLoading={false}
      onError={(err) => {
        console.error('❌ PayPal Script Provider Error:', err);
        setError('Failed to load PayPal. Please refresh the page or contact support.');
      }}
      onLoad={() => {
        console.log('✅ PayPal SDK loaded successfully');
        console.log('Client ID:', PAYPAL_CLIENT_ID ? `${PAYPAL_CLIENT_ID.substring(0, 20)}...` : 'NOT SET');
      }}
    >
      <Helmet>
        <title>Purchase Gift Cards | Mystic Goddess Beauty Studio</title>
        <meta name="description" content="Give the gift of beauty and transformation. Purchase a Mystic Goddess Beauty Studio gift card for your loved ones." />
        <link rel="canonical" href="https://mysticgoddessbeauty.com/gift-cards" />
      </Helmet>
      <div className="gift-cards-page">
        <div className="gift-cards-hero">
          <div className="stars-container-gift">
            {[...Array(30)].map((_, i) => (
              <div
                key={i}
                className="star"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 3}s`
                }}
              />
            ))}
          </div>
          <Button
            variant="ghost"
            className="back-to-home"
            onClick={() => navigate('/')}
          >
            ← Back to Home
          </Button>
          <div className="gift-hero-content">
            <Gift className="gift-icon-hero" />
            <h1 className="gift-hero-title">Mystic Goddess Gift Cards</h1>
            <p className="gift-hero-subtitle">The Perfect Gift of Transformation</p>
            <p className="gift-hero-description">
              Give the gift of celestial beauty. Perfect for birthdays, celebrations, or just because.
            </p>
          </div>
        </div>

        <div className="gift-cards-content">
          <div className="gift-cards-container">
            
            {error && (
              <div style={{
                padding: '1rem',
                marginBottom: '2rem',
                background: '#fee2e2',
                border: '2px solid #ef4444',
                borderRadius: '8px',
                color: '#991b1b',
                textAlign: 'center'
              }}>
                {error}
              </div>
            )}

            {showPreview ? (
              <>
                <div className="gift-section">
                  <h2 className="section-title-gift">
                    <Check className="section-icon-inline" />
                    Review Your Gift Card
                  </h2>
                  
                  <Card className="details-card">
                    <CardContent className="details-content">
                      <div style={{ marginBottom: '2rem' }}>
                        <h3 style={{ fontSize: '1.5rem', color: 'var(--mystic-purple)', marginBottom: '1rem' }}>
                          Gift Card Details
                        </h3>
                        <div style={{ display: 'grid', gap: '1rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #e5e5e5' }}>
                            <span style={{ fontWeight: '600' }}>Amount:</span>
                            <span style={{ color: 'var(--mystic-gold)', fontSize: '1.2rem', fontWeight: '700' }}>${selectedAmount}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #e5e5e5' }}>
                            <span style={{ fontWeight: '600' }}>Recipient:</span>
                            <span>{recipientName}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #e5e5e5' }}>
                            <span style={{ fontWeight: '600' }}>Email:</span>
                            <span>{recipientEmail}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #e5e5e5' }}>
                            <span style={{ fontWeight: '600' }}>From:</span>
                            <span>{senderName}</span>
                          </div>
                          {message && (
                            <div style={{ padding: '1rem', background: 'var(--bg-cream)', borderRadius: '8px', marginTop: '1rem' }}>
                              <span style={{ fontWeight: '600', display: 'block', marginBottom: '0.5rem' }}>Message:</span>
                              <span style={{ fontStyle: 'italic' }}>{message}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="summary-card" style={{ marginTop: '2rem' }}>
                        <div className="summary-content">
                          <h3 style={{ fontSize: '1.3rem', color: 'var(--mystic-purple)', marginBottom: '1rem' }}>
                            Complete Your Purchase
                          </h3>
                          
                          {isProcessing ? (
                            <div style={{ textAlign: 'center', padding: '2rem' }}>
                              <Loader2 className="w-8 h-8 animate-spin" style={{ margin: '0 auto', color: 'var(--mystic-gold)' }} />
                              <p style={{ marginTop: '1rem' }}>Processing payment...</p>
                            </div>
                          ) : (
                            <>
                              <Button
                                onClick={handlePayPalPayment}
                                disabled={isProcessing}
                                style={{
                                  width: '100%',
                                  padding: '1rem 2rem',
                                  fontSize: '1.1rem',
                                  fontWeight: 'bold',
                                  background: isProcessing ? '#ccc' : '#0070ba',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '8px',
                                  cursor: isProcessing ? 'not-allowed' : 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '0.5rem',
                                  marginBottom: '1rem'
                                }}
                              >
                                {isProcessing ? (
                                  <>
                                    <Loader2 className="animate-spin" size={20} />
                                    Connecting to PayPal...
                                  </>
                                ) : (
                                  <>
                                    💳 Continue to PayPal
                                  </>
                                )}
                              </Button>
                              
                              <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                                <Button 
                                  variant="ghost" 
                                  onClick={() => setShowPreview(false)}
                                  style={{ color: 'var(--mystic-purple)' }}
                                >
                                  ← Back to Edit
                                </Button>
                              </div>
                            </>
                          )}

                          <div className="payment-notice">
                            🔒 Secure payment powered by PayPal. Your information is protected.
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </>
            ) : (
              <>
                {/* Amount Selection */}
                <div className="gift-section">
                  <h2 className="section-title-gift">
                    <Gift className="section-icon-inline" />
                    Select Amount
                  </h2>
                  <div className="amount-grid">
                    {amounts.map((amount) => (
                      <Card
                        key={amount.value}
                        className={`amount-card ${selectedAmount === amount.value ? 'selected' : ''} ${amount.popular ? 'popular' : ''}`}
                        onClick={() => setSelectedAmount(amount.value)}
                      >
                        {amount.popular && <div className="popular-badge">Most Popular</div>}
                        <CardContent className="amount-card-content">
                          <div className="amount-value">${amount.value}</div>
                          <p className="amount-description">Perfect for any service</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>

                {/* Delivery Options */}
                <div className="gift-section">
                  <h2 className="section-title-gift">
                    <Mail className="section-icon-inline" />
                    Delivery Method
                  </h2>
                  <div className="delivery-options">
                    <Card
                      className={`delivery-card ${deliveryType === 'email' ? 'selected' : ''}`}
                      onClick={() => setDeliveryType('email')}
                    >
                      <CardContent className="delivery-content">
                        <Mail className="delivery-icon w-8 h-8" />
                        <h3>Email Delivery</h3>
                        <p>Instant delivery to recipient's email</p>
                      </CardContent>
                    </Card>
                    
                    <Card
                      className={`delivery-card ${deliveryType === 'printable' ? 'selected' : ''}`}
                      onClick={() => setDeliveryType('printable')}
                    >
                      <CardContent className="delivery-content">
                        <Download className="delivery-icon w-8 h-8" />
                        <h3>Printable</h3>
                        <p>Download and print yourself</p>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                {/* Gift Card Details Form */}
                <div className="gift-section">
                  <h2 className="section-title-gift">
                    <User className="section-icon-inline" />
                    Gift Card Details
                  </h2>
                  <Card className="details-card">
                    <CardContent className="details-content">
                      <div className="form-grid">
                        <div className="form-field">
                          <Label htmlFor="recipientName">Recipient Name *</Label>
                          <Input
                            id="recipientName"
                            placeholder="Who is this for?"
                            value={recipientName}
                            onChange={(e) => setRecipientName(e.target.value)}
                            required
                          />
                        </div>

                        <div className="form-field">
                          <Label htmlFor="recipientEmail">Recipient Email *</Label>
                          <Input
                            id="recipientEmail"
                            type="email"
                            placeholder="their@email.com"
                            value={recipientEmail}
                            onChange={(e) => setRecipientEmail(e.target.value)}
                            required
                          />
                        </div>

                        <div className="form-field full-width">
                          <Label htmlFor="senderName">Your Name *</Label>
                          <Input
                            id="senderName"
                            placeholder="From who?"
                            value={senderName}
                            onChange={(e) => setSenderName(e.target.value)}
                            required
                          />
                        </div>

                        <div className="form-field full-width">
                          <Label htmlFor="message">Personal Message (Optional)</Label>
                          <Textarea
                            id="message"
                            placeholder="Add a personal touch..."
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            rows={4}
                          />
                        </div>
                      </div>

                      <div className="purchase-buttons">
                        <Button
                          className="purchase-btn-main"
                          onClick={handlePreview}
                          disabled={!selectedAmount || !recipientName || !recipientEmail || !senderName}
                        >
                          <CreditCard className="w-5 h-5 mr-2" />
                          Continue to Payment
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* How It Works */}
                <div className="info-section">
                  <h2 className="section-title-gift">
                    <Sparkles className="section-icon-inline" />
                    How It Works
                  </h2>
                  <div className="info-grid">
                    <Card className="info-card">
                      <CardContent className="info-content">
                        <div className="info-number">1</div>
                        <h3>Choose Amount</h3>
                        <p>Select the perfect gift card value</p>
                      </CardContent>
                    </Card>

                    <Card className="info-card">
                      <CardContent className="info-content">
                        <div className="info-number">2</div>
                        <h3>Add Details</h3>
                        <p>Personalize with a message</p>
                      </CardContent>
                    </Card>

                    <Card className="info-card">
                      <CardContent className="info-content">
                        <div className="info-number">3</div>
                        <h3>Complete Payment</h3>
                        <p>Secure checkout with PayPal</p>
                      </CardContent>
                    </Card>

                    <Card className="info-card">
                      <CardContent className="info-content">
                        <div className="info-number">4</div>
                        <h3>Instant Delivery</h3>
                        <p>Recipient gets email immediately</p>
                      </CardContent>
                    </Card>
                  </div>
                </div>

                {/* Terms */}
                <div className="gift-section">
                  <Card className="terms-card">
                    <CardContent className="terms-content">
                      <h3 className="section-title-gift">Terms & Conditions</h3>
                      <ul className="terms-list">
                        <li>✓ Gift cards never expire</li>
                        <li>✓ Valid for all services at Mystic Goddess Beauty Studio</li>
                        <li>✓ Can be used for multiple visits until balance is zero</li>
                        <li>✓ Non-refundable and cannot be exchanged for cash</li>
                        <li>✓ Present code at time of booking or service</li>
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </PayPalScriptProvider>
  );
};

export default GiftCards;
